// app/javascript/components/student/SyllabusTreeModal.tsx
import { useState, useMemo, useEffect } from 'react'
import { Link } from '@inertiajs/react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import {
  BookOpen, Layers, ChevronRight, ChevronDown,
  Search, Play, Lock, RotateCcw, X, Folder, FileText
} from 'lucide-react'
import type { TestItem } from '@/components/student/TestCard'

export interface SyllabusChapter {
  id: number
  name: string
  question_count: number
}

export interface SyllabusSubject {
  id: number
  name: string
  question_count: number
  chapters: SyllabusChapter[]
}

export interface SyllabusSection {
  id: number
  name: string
  total_questions: number
  duration_minutes?: number
  subjects: SyllabusSubject[]
}

interface Props {
  test: TestItem | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function SyllabusTreeModal({ test, open, onOpenChange }: Props) {
  const [search, setSearch] = useState('')
  const [collapsedSections, setCollapsedSections] = useState<Record<number, boolean>>({})

  const syllabusTree: SyllabusSection[] = useMemo(() => {
    return (test?.syllabus_tree as SyllabusSection[]) || []
  }, [test])

  // Reset search and expand all sections when test changes or modal opens
  useEffect(() => {
    if (open) {
      setSearch('')
      setCollapsedSections({})
    }
  }, [open, test?.id])

  const toggleSection = (id: number) => {
    setCollapsedSections(prev => ({ ...prev, [id]: !prev[id] }))
  }

  // Filter tree based on search query
  const filteredTree = useMemo(() => {
    if (!search.trim()) return syllabusTree
    const q = search.toLowerCase()

    return syllabusTree
      .map(sec => {
        const matchingSubjects = (sec.subjects || [])
          .map(sub => {
            const matchingChapters = (sub.chapters || []).filter(ch =>
              ch.name.toLowerCase().includes(q)
            )
            const subMatches = (sub.name || '').toLowerCase().includes(q)

            if (subMatches || matchingChapters.length > 0) {
              return {
                ...sub,
                chapters: subMatches ? (sub.chapters || []) : matchingChapters
              }
            }
            return null
          })
          .filter(Boolean) as SyllabusSubject[]

        const secMatches = (sec.name || '').toLowerCase().includes(q)

        if (secMatches || matchingSubjects.length > 0) {
          return {
            ...sec,
            subjects: secMatches ? (sec.subjects || []) : matchingSubjects
          }
        }
        return null
      })
      .filter(Boolean) as SyllabusSection[]
  }, [syllabusTree, search])

  // Summary counts
  const totalChapters = useMemo(() => {
    let count = 0
    syllabusTree.forEach(sec => {
      ;(sec.subjects || []).forEach(sub => {
        count += (sub.chapters || []).length
      })
    })
    return count
  }, [syllabusTree])

  const filteredChaptersCount = useMemo(() => {
    let count = 0
    filteredTree.forEach(sec => {
      ;(sec.subjects || []).forEach(sub => {
        count += (sub.chapters || []).length
      })
    })
    return count
  }, [filteredTree])

  const allCollapsed = useMemo(() => {
    if (syllabusTree.length === 0) return false
    return syllabusTree.every(sec => collapsedSections[sec.id])
  }, [syllabusTree, collapsedSections])

  const toggleAll = () => {
    if (allCollapsed) {
      setCollapsedSections({})
    } else {
      const next: Record<number, boolean> = {}
      syllabusTree.forEach(sec => {
        next[sec.id] = true
      })
      setCollapsedSections(next)
    }
  }

  if (!test) return null

  const isUnlocked = test.has_access || test.is_free
  const hasAttempted = Boolean(test.attempt_data?.has_attempted)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-1.5rem)] sm:max-w-2xl max-h-[90dvh] flex flex-col p-0 overflow-hidden rounded-2xl sm:rounded-3xl border border-border bg-card shadow-glass-lg">
        {/* Header - pr-12 prevents collision with the top-right X button */}
        <DialogHeader className="p-4 sm:p-6 pb-3 sm:pb-4 border-b border-border shrink-0 space-y-3 pr-12 text-left">
          <div className="space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="h-7 w-7 rounded-lg bg-primary/10 text-primary border border-primary/20 flex items-center justify-center shrink-0">
                <BookOpen className="h-3.5 w-3.5" />
              </span>
              <DialogTitle className="text-base sm:text-lg font-bold text-foreground leading-snug">
                Test Syllabus
              </DialogTitle>
              {test.test_type && (
                <Badge variant="outline" className="text-[10px] font-bold uppercase tracking-wider bg-muted text-muted-foreground border-border/80 px-2 py-0.5 shrink-0 hidden sm:inline-flex">
                  {test.test_type.replace(/_/g, ' ')}
                </Badge>
              )}
            </div>

            <DialogDescription className="text-xs text-muted-foreground font-medium line-clamp-1">
              {test.exam_name ? `${test.exam_name} · ` : ''}{test.title}
            </DialogDescription>
          </div>

          {/* Quick Stats Strip */}
          <div className="flex items-center flex-wrap gap-2 text-xs">
            <span className="inline-flex items-center gap-1.5 font-semibold bg-muted text-foreground px-2.5 py-1 rounded-lg border border-border/60">
              <Layers className="h-3.5 w-3.5 text-primary shrink-0" />
              {syllabusTree.length} Section{syllabusTree.length !== 1 ? 's' : ''}
            </span>
            <span className="inline-flex items-center gap-1.5 font-semibold bg-muted text-foreground px-2.5 py-1 rounded-lg border border-border/60">
              <FileText className="h-3.5 w-3.5 text-secondary shrink-0" />
              {totalChapters} Chapter{totalChapters !== 1 ? 's' : ''}
            </span>
            {test.question_count > 0 && (
              <span className="text-[11px] font-medium text-muted-foreground px-1 py-0.5">
                • {test.question_count} Questions
              </span>
            )}
            {test.duration_minutes > 0 && (
              <span className="text-[11px] font-medium text-muted-foreground px-1 py-0.5">
                • {test.duration_minutes} Mins
              </span>
            )}
          </div>

          {/* Search and Expand/Collapse Controls */}
          <div className="flex items-center gap-2 pt-0.5">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
              <Input
                type="text"
                placeholder="Search chapters or topics..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 pr-7 h-8.5 rounded-lg bg-background border-border text-xs focus-visible:ring-primary placeholder:text-muted-foreground"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-0.5 transition-colors"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            {syllabusTree.length > 1 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={toggleAll}
                className="h-8.5 px-2 text-xs font-semibold text-muted-foreground hover:text-foreground shrink-0 cursor-pointer"
              >
                {allCollapsed ? 'Expand all' : 'Collapse all'}
              </Button>
            )}
          </div>

          {search.trim() && (
            <p className="text-[11px] text-muted-foreground pt-0.5">
              Found <span className="font-semibold text-foreground">{filteredChaptersCount}</span> matching {filteredChaptersCount === 1 ? 'chapter' : 'chapters'} across <span className="font-semibold text-foreground">{filteredTree.length}</span> {filteredTree.length === 1 ? 'section' : 'sections'}
            </p>
          )}
        </DialogHeader>

        {/* Tree Content (Scrollable) */}
        <div className="flex-1 overflow-y-auto p-3.5 sm:p-6 space-y-3 min-h-0">
          {filteredTree.length > 0 ? (
            <div className="space-y-3">
              {filteredTree.map((sec) => {
                const isCollapsed = Boolean(collapsedSections[sec.id])
                const sectionChapterCount = (sec.subjects || []).reduce(
                  (acc, sub) => acc + (sub.chapters || []).length,
                  0
                )

                return (
                  <div
                    key={sec.id}
                    className="rounded-xl sm:rounded-2xl border border-border bg-card overflow-hidden transition-all shadow-2xs"
                  >
                    {/* Section Header Row (Collapsible) */}
                    <button
                      type="button"
                      onClick={() => toggleSection(sec.id)}
                      className="w-full flex items-center justify-between gap-2 p-3 sm:p-4 bg-muted/40 hover:bg-muted/70 transition-colors text-left cursor-pointer border-b border-border/50"
                    >
                      <div className="flex items-center gap-2.5 min-w-0 flex-1">
                        {isCollapsed ? (
                          <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-primary shrink-0" />
                        )}
                        <span className="font-bold text-xs sm:text-sm text-foreground break-words leading-tight">
                          {sec.name}
                        </span>
                      </div>

                      <div className="flex items-center gap-1.5 shrink-0">
                        {sec.duration_minutes && (
                          <span className="text-[10px] sm:text-xs font-mono font-medium text-muted-foreground bg-muted px-1.5 sm:px-2 py-0.5 rounded border border-border/60">
                            {sec.duration_minutes}m
                          </span>
                        )}
                        <span className="text-[10px] sm:text-xs font-semibold text-muted-foreground bg-muted px-2 py-0.5 rounded-md border border-border/60">
                          {sectionChapterCount} {sectionChapterCount === 1 ? 'chapter' : 'chapters'}
                        </span>
                      </div>
                    </button>

                    {/* Section Children: Subjects & Chapters */}
                    {!isCollapsed && (
                      <div className="p-3 sm:p-4 space-y-3">
                        {sec.subjects.map((sub) => {
                          // Check if subject is identical to section name or single subject
                          const isRedundantSubject =
                            sec.subjects.length === 1 &&
                            (!sub.name || sub.name.toLowerCase() === sec.name.toLowerCase() || sub.name.toLowerCase() === 'general')

                          return (
                            <div key={sub.id} className="space-y-2">
                              {/* Only render subject sub-header when there are multiple or distinct subjects */}
                              {!isRedundantSubject && (
                                <div className="flex items-center justify-between gap-2 pb-1 text-xs font-semibold text-muted-foreground border-b border-border/40">
                                  <div className="flex items-center gap-1.5">
                                    <Folder className="h-3.5 w-3.5 text-secondary shrink-0" />
                                    <span className="text-foreground">{sub.name}</span>
                                  </div>
                                  <span className="text-[11px]">
                                    {sub.chapters.length} {sub.chapters.length === 1 ? 'chapter' : 'chapters'}
                                  </span>
                                </div>
                              )}

                              {/* Chapters Grid - 1 col on mobile, 2 cols on tablet/desktop */}
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {sub.chapters.map((ch, idx) => (
                                  <div
                                    key={ch.id || idx}
                                    className="flex items-start gap-2.5 p-2.5 rounded-xl bg-muted/20 hover:bg-muted/50 border border-border/60 hover:border-primary/40 transition-colors"
                                  >
                                    <span className="h-1.5 w-1.5 rounded-full bg-primary/70 shrink-0 mt-2" />
                                    <div className="flex-1 min-w-0 flex items-start justify-between gap-2">
                                      <span className="text-xs font-medium text-foreground leading-snug break-words">
                                        {ch.name}
                                      </span>
                                      {ch.question_count > 0 && (
                                        <span className="text-[10px] font-mono text-muted-foreground shrink-0 tabular-nums pt-0.5">
                                          ~{ch.question_count} Qs
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          ) : (
            /* Search Empty State */
            <div className="py-12 text-center space-y-2">
              <p className="text-sm font-bold text-foreground">No matching topics or chapters found</p>
              <p className="text-xs text-muted-foreground">
                Try searching for another chapter keyword or clear the search filter.
              </p>
              {search && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSearch('')}
                  className="rounded-xl border-border text-xs font-semibold mt-2"
                >
                  Clear Search Filter
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Footer Actions - Side-by-side on mobile, right-aligned on desktop */}
        <DialogFooter className="p-3 sm:p-4 border-t border-border bg-card flex flex-row items-center justify-between sm:justify-end gap-2 shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            className="rounded-xl border-border font-semibold text-xs h-9 px-4 flex-1 sm:flex-initial"
          >
            Close
          </Button>

          <Button
            size="sm"
            asChild
            className={`rounded-xl font-bold text-xs h-9 px-4 gap-1.5 shadow-xs flex-1 sm:flex-initial ${
              isUnlocked
                ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                : 'bg-tertiary text-tertiary-foreground hover:bg-tertiary/90'
            }`}
          >
            <Link href={`/student/tests/${test.id}`}>
              {isUnlocked ? (
                hasAttempted ? (
                  <>
                    <RotateCcw className="h-3.5 w-3.5" /> Retake Test
                  </>
                ) : (
                  <>
                    <Play className="h-3.5 w-3.5 fill-current" /> Start Test
                  </>
                )
              ) : (
                <>
                  <Lock className="h-3.5 w-3.5" /> Unlock Test ({test.credit_cost} Credits)
                </>
              )}
            </Link>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
